import { useState } from 'react';

import { useNotify } from '@/api/notify';
import { AuthLayout } from '@/components/AuthLayout';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { controlClass, Field } from '@/components/Field';
import { Button } from '@/components/PageHeading';

import { changePlatformPassword } from './api';
import { platformSignIn, type PlatformAuth } from './client';

const panel = {
  heading: 'AttireRent platform',
  blurb: 'Companies, packages and setup. Nothing here is reachable from a shop’s own screens.',
};

/**
 * The platform's own sign-in — an email and a password, no company code, because the person signing
 * in here runs the platform rather than a shop.
 *
 * <p>
 * The forced first password change happens here too, replacing the server script that used to be the
 * only way through it. Changing the password revokes every platform session, this one included, so
 * the new password is used to sign straight back in rather than leaving the person on a screen whose
 * session has just ended under them.
 * </p>
 */
export function PlatformSignIn({ onSignedIn }: { onSignedIn: (auth: PlatformAuth) => void }) {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mustChange, setMustChange] = useState(false);
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const first = useAutoFocusFirstField<HTMLFormElement>();

  const signIn = async () => {
    setBusy(true);
    try {
      const auth = await platformSignIn(email.trim(), password);
      if (auth.user.mustChangePassword) {
        setMustChange(true);
      } else {
        onSignedIn(auth);
      }
    } catch (error) {
      notify.error(error);
    } finally {
      setBusy(false);
    }
  };

  const change = async () => {
    if (next !== again) {
      notify.warning('The two new passwords are different.');
      return;
    }
    setBusy(true);
    try {
      await changePlatformPassword(password, next);
      onSignedIn(await platformSignIn(email.trim(), next));
    } catch (error) {
      notify.error(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      panel={panel}
      title={mustChange ? 'Choose your password' : 'Platform sign-in'}
      subtitle={
        mustChange
          ? 'The password you signed in with was set for you. Replace it before going further.'
          : 'For the people who run AttireRent, not for shops.'
      }
    >
      <form
        ref={first}
        {...enterKeyScope}
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void (mustChange ? change() : signIn());
        }}
      >
        {!mustChange ? (
          <>
            <Field label="Email" required>
              <input
                className={controlClass}
                type="email"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
            <Field label="Password" required>
              <input
                className={controlClass}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
          </>
        ) : (
          <>
            <Field label="New password" hint="At least 12 characters." required>
              <input
                className={controlClass}
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(event) => setNext(event.target.value)}
              />
            </Field>
            <Field label="New password again" required>
              <input
                className={controlClass}
                type="password"
                autoComplete="new-password"
                value={again}
                onChange={(event) => setAgain(event.target.value)}
              />
            </Field>
          </>
        )}

        <Button
          type="submit"
          variant="primary"
          data-enter-key-commit
          loading={busy}
          disabled={busy || (mustChange ? !next || !again : !email.trim() || !password)}
          className="justify-center"
        >
          {busy ? 'Working…' : mustChange ? 'Save and continue' : 'Sign in'}
        </Button>
      </form>
    </AuthLayout>
  );
}
